'use client';

// The command is the hero, so it has to be readable across the room and
// copyable in one click — the whole surface is the button, not a small icon
// beside it.
import { useCallback, useState } from 'react';

const COPIED_FEEDBACK_MS = 1600;

export function CopyCommand({ command }: { command: string }) {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(async () => {
    await navigator.clipboard.writeText(command);
    setCopied(true);
    setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
  }, [command]);

  return (
    <button
      aria-label={`Copy: ${command}`}
      className="group flex w-full items-center gap-4 rounded-2xl border border-bnk-stroke-subtle bg-bnk-surface-0 px-5 py-5 text-left transition-colors hover:border-bnk-brand/60 md:gap-6 md:px-8 md:py-7"
      onClick={copy}
      type="button"
    >
      <span aria-hidden className="shrink-0 font-mono text-lg text-bnk-brand md:text-2xl">
        $
      </span>
      {/* One line is what makes it read as a command, so it stays on one wherever
          it fits. On a narrow phone it wraps instead of truncating — a command
          you can only half-read is worse than one on two lines. */}
      <code className="min-w-0 flex-1 break-words font-mono text-sm text-bnk-text sm:whitespace-nowrap sm:text-base md:text-xl lg:text-2xl">
        {command}
      </code>
      <span
        className={`shrink-0 font-satoshi text-sm font-medium transition-colors ${
          copied ? 'text-bnk-brand' : 'text-bnk-text-tertiary group-hover:text-bnk-text-secondary'
        }`}
      >
        {copied ? 'Copied' : 'Copy'}
      </span>
    </button>
  );
}
